import { type FC, useEffect, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import HostedVideoPlayer from "@forento/shared/components/HostedVideoPlayer"
import LexicalViewer from "@forento/shared/components/LexicalViewer"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/original/components/Button"
import Hero, { CallToActionButtons, CallToActionInfo } from "~/themes/original/components/Hero"
import Layout from "~/themes/original/components/Layout"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import routes from "~/utilities/routes"
import { lightTextColor } from "~/utilities/styles"
import { query } from "~/utilities/trpc"

const VideoPage: FC = () => {
	const { videoId } = useParams()
	const user = useUser()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const { data: video, error, refetch } = query.video.getPersonalized.useQuery({ videoId: Number(videoId) })

	useEffect(() => {
		refetch()
	}, [refetch, user.user])

	if (video === null) return <Navigate to={routes.video.index()} />

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "video" }} paypalOrder={paypalOrder}>
			<Layout>
				{error ? (
					<p>Failed to load video.</p>
				) : video === undefined ? (
					<PartialLoadingPage />
				) : (
					<>
						{video.status === "unlocked" ? (
							<Container>
								<StyledVideoPlayer video={video} />
								<Title>{video.name}</Title>
								{video.longDescriptionHtml && <Description html={video.longDescriptionHtml} />}
							</Container>
						) : (
							<Hero
								title={video.name}
								thumbnailUrl={video.thumbnailUrl}
								htmlDescription={video.longDescriptionHtml}
								tags={video.tags}
								callToAction={
									video.status === "membership-required" ? (
										<>
											<CallToActionInfo>
												This video is only available through a membership.
											</CallToActionInfo>
											<Button variant="primary" onClick={routes.membership.index()}>
												Memberships
											</Button>
										</>
									) : video.status === "paywall" ? (
										<PurchaseItem
											item={{
												type: "video",
												id: video.id,
												name: video.name,
												shortDescription: video.shortDescription,
												price: video.price,
											}}
											onPurchase={async ({ paypalOrder }) => {
												setPaypalOrder(paypalOrder)
												await refetch()
											}}
										/>
									) : video.status === "sign-in-required" ? (
										<>
											<CallToActionInfo>
												This video is only available to signed in users.
											</CallToActionInfo>
											<CallToActionButtons>
												<Button variant="primary" onClick={routes.user.signin()}>
													Sign in
												</Button>
												<Button variant="primary" onClick={routes.user.signup()}>
													Sign up
												</Button>
											</CallToActionButtons>
										</>
									) : video.status === "paused" ? (
										<CallToActionInfo>Sales are paused.</CallToActionInfo>
									) : (
										exhaustiveGuard(video.status)
									)
								}
							/>
						)}
						{video.presentationHtml !== null && (
							<div className="editorjs" dangerouslySetInnerHTML={{ __html: video.presentationHtml }} />
						)}
					</>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

const Container = styled.div`
	margin-bottom: 42px;
`

const StyledVideoPlayer = styled(HostedVideoPlayer)`
	background-color: #999;
	margin-bottom: 16px;
`

const Title = styled.h1`
	font-weight: bold;
	font-size: 28px;
	color: #444a6d;
	margin-bottom: 12px;

	@media (max-width: 999px) {
		padding: 0 16px;
	}
`

const Description = styled(LexicalViewer)`
	color: ${lightTextColor};

	@media (max-width: 999px) {
		padding: 0 16px;
	}
`

export default VideoPage
